import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/useAuth'
import { usePreferences } from '../context/usePreferences'
import { useTranslation } from '../context/useTranslation'
import { ACCENT_OPTIONS, LANGUAGE_OPTIONS } from '../context/preferencesConstants'
import { api } from '../api/client'
import { useToast } from '../context/useToast'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import SectionTitle from '../components/ui/SectionTitle'
import DataTable, { DataTableHead, DataTableBody, DataTableRow, DataTableCell } from '../components/ui/DataTable'
import { PrimaryButton, SecondaryButton, DangerButton } from '../components/ui/Button'
import Badge from '../components/ui/Badge'

export default function Settings() {
  const { user, token, refreshUser, logout } = useAuth()
  const { t } = useTranslation()
  const prefs = usePreferences()
  const navigate = useNavigate()
  const toast = useToast()
  const [activeTab, setActiveTab] = useState('general')
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userError, setUserError] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [formEmail, setFormEmail] = useState('')
  const [formPassword, setFormPassword] = useState('')
  const [formIsAdmin, setFormIsAdmin] = useState(false)
  const [formIsActive, setFormIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [brandingData, setBrandingData] = useState(null)
  const [brandingLoading, setBrandingLoading] = useState(false)
  const [brandingError, setBrandingError] = useState('')
  const [brandingSaving, setBrandingSaving] = useState(false)
  const [brandingForm, setBrandingForm] = useState({})
  const [logoUploading, setLogoUploading] = useState(false)
  const [emailOnJobDone, setEmailOnJobDone] = useState(true)
  const [emailOnJobDoneSaving, setEmailOnJobDoneSaving] = useState(false)

  const isAdmin = user?.is_admin === true

  const loadUsers = () => {
    if (!isAdmin) return
    setUsersLoading(true)
    setUserError('')
    api.get('/users')
      .then((r) => setUsers(r.data || []))
      .catch((e) => setUserError(e.message || 'Fehler beim Laden'))
      .finally(() => setUsersLoading(false))
  }

  useEffect(() => {
    if (isAdmin && activeTab === 'users') loadUsers()
  }, [isAdmin, activeTab])

  useEffect(() => {
    if (activeTab === 'profile' && token) {
      api.get('/me').then((r) => {
        const v = r.data?.email_on_job_done
        setEmailOnJobDone(v !== false)
      }).catch(() => {})
    }
  }, [activeTab])

  const loadBranding = () => {
    setBrandingLoading(true)
    setBrandingError('')
    api.get('/me/branding')
      .then((r) => {
        const d = r.data || {}
        setBrandingData(d)
        setBrandingForm({
          brand_display_name: d.brand_display_name ?? '',
          brand_tagline: d.brand_tagline ?? '',
          palette_primary: d.palette_primary ?? '',
          palette_accent: d.palette_accent ?? '',
          palette_background: d.palette_background ?? '',
          palette_text: d.palette_text ?? '',
          font_primary: d.font_primary ?? 'Inter',
          font_secondary: d.font_secondary ?? 'Inter',
          cover_layout_preset: d.cover_layout_preset ?? 'center',
          cover_text_style: d.cover_text_style ?? 'minimal',
          brand_prompt: d.brand_prompt ?? '',
          default_genre_style: d.default_genre_style ?? 'melodic_house',
          watermark_enabled: d.watermark_enabled ?? false,
          watermark_text: d.watermark_text ?? '',
        })
      })
      .catch((e) => setBrandingError(e.message || 'Fehler beim Laden'))
      .finally(() => setBrandingLoading(false))
  }

  useEffect(() => {
    if (activeTab === 'branding') loadBranding()
  }, [activeTab])

  const saveBranding = async (e) => {
    e.preventDefault()
    setBrandingSaving(true)
    setBrandingError('')
    try {
      const body = {
        brand_display_name: brandingForm.brand_display_name?.trim() || null,
        brand_tagline: brandingForm.brand_tagline?.trim() || null,
        palette_primary: brandingForm.palette_primary?.trim() || null,
        palette_accent: brandingForm.palette_accent?.trim() || null,
        palette_background: brandingForm.palette_background?.trim() || null,
        palette_text: brandingForm.palette_text?.trim() || null,
        font_primary: brandingForm.font_primary || null,
        font_secondary: brandingForm.font_secondary || null,
        cover_layout_preset: brandingForm.cover_layout_preset || null,
        cover_text_style: brandingForm.cover_text_style || null,
        brand_prompt: brandingForm.brand_prompt?.trim() || null,
        default_genre_style: brandingForm.default_genre_style || null,
        watermark_enabled: brandingForm.watermark_enabled ?? false,
        watermark_text: brandingForm.watermark_text?.trim() || null,
      }
      const { data } = await api.put('/me/branding', body)
      setBrandingData(data)
      toast('Branding gespeichert', 'success')
    } catch (err) {
      setBrandingError(err.message || 'Speichern fehlgeschlagen')
      toast(err.message || 'Speichern fehlgeschlagen', 'error')
    } finally {
      setBrandingSaving(false)
    }
  }

  const uploadBrandLogo = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toast('Logo max. 2 MB', 'error')
      return
    }
    setLogoUploading(true)
    setBrandingError('')
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api.post('/me/branding/logo', form)
      setBrandingData(data)
      setBrandingForm((f) => ({ ...f, brand_logo: data.brand_logo }))
      toast('Logo hochgeladen', 'success')
    } catch (err) {
      setBrandingError(err.message || 'Upload fehlgeschlagen')
      toast(err.message || 'Upload fehlgeschlagen', 'error')
    } finally {
      setLogoUploading(false)
    }
  }

  const openCreate = () => {
    setEditId(null)
    setFormEmail('')
    setFormPassword('')
    setFormIsAdmin(false)
    setFormIsActive(true)
    setFormOpen(true)
  }

  const openEdit = (u) => {
    setEditId(u.id)
    setFormEmail(u.email)
    setFormPassword('')
    setFormIsAdmin(u.is_admin)
    setFormIsActive(u.is_active)
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditId(null)
    setUserError('')
  }

  const saveUser = async (e) => {
    e.preventDefault()
    setSaving(true)
    setUserError('')
    try {
      if (editId) {
        const body = { is_active: formIsActive, is_admin: formIsAdmin }
        if (formEmail.trim()) body.email = formEmail.trim()
        if (formPassword.trim()) body.password = formPassword
        await api.patch(`/users/${editId}`, body)
      } else {
        if (!formEmail.trim() || !formPassword.trim()) {
          setUserError('E-Mail und Passwort angeben.')
          setSaving(false)
          return
        }
        await api.post('/users', { email: formEmail.trim(), password: formPassword, is_admin: formIsAdmin })
      }
      closeForm()
      if (editId === user?.id) {
        logout()
        navigate('/login?email_changed=1', { replace: true })
        return
      }
      refreshUser()
      loadUsers()
      toast('Benutzer gespeichert', 'success')
    } catch (err) {
      setUserError(err.message || 'Speichern fehlgeschlagen')
      toast(err.message || 'Speichern fehlgeschlagen', 'error')
    } finally {
      setSaving(false)
    }
  }

  const deleteUser = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return
    setUserError('')
    try {
      await api.delete(`/users/${id}`)
      loadUsers()
      toast('Benutzer gelöscht', 'success')
    } catch (err) {
      setUserError(err.message || 'Löschen fehlgeschlagen')
      toast(err.message || 'Löschen fehlgeschlagen', 'error')
    }
  }

  const [exportLoading, setExportLoading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const handleExportData = async () => {
    setExportLoading(true)
    try {
      const { data } = await api.get('/me/export')
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ai-mastering-studio-daten-export-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast('Daten exportiert', 'success')
    } catch (err) {
      toast(err?.message || 'Export fehlgeschlagen', 'error')
    } finally {
      setExportLoading(false)
    }
  }

  const handleDeleteAccount = async (e) => {
    e.preventDefault()
    if (deleteConfirm !== 'KONTO LÖSCHEN' || !deletePassword.trim()) {
      setDeleteError('Bitte Passwort eingeben und exakt "KONTO LÖSCHEN" zur Bestätigung.')
      return
    }
    setDeleteLoading(true)
    setDeleteError('')
    try {
      await api.post('/me/delete-account', { password: deletePassword, confirmation: deleteConfirm })
      toast('Konto wurde gelöscht.', 'success')
      logout()
      navigate('/login', { replace: true })
    } catch (err) {
      setDeleteError(err?.message || 'Löschung fehlgeschlagen.')
      toast(err?.message || 'Löschung fehlgeschlagen', 'error')
    } finally {
      setDeleteLoading(false)
    }
  }

  const tabs = [
    { id: 'general', label: t('settings.general') },
    { id: 'profile', label: t('settings.profile') },
    { id: 'branding', label: 'Branding' },
    { id: 'privacy', label: 'Datenschutz & Rechte' },
    ...(isAdmin ? [{ id: 'users', label: t('settings.users') }] : []),
  ]

  return (
    <PageShell>
      <div className="space-y-8">
        <h1 className="text-2xl font-bold text-white">{t('settings.title')}</h1>

        <div className="rounded-xl bg-white/5 p-1">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-lg px-4 py-2.5 text-sm font-medium transition ${
                  activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'general' && (
          <Card>
            <SectionTitle className="mb-4">{t('settings.general')}</SectionTitle>
            <p className="mb-6 text-sm text-white/60">
              {t('settings.generalIntro')}
            </p>
            <div className="space-y-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">{t('settings.language')}</label>
                <select
                  value={prefs.language}
                  onChange={(e) => prefs.setLanguage(e.target.value)}
                  className="w-full max-w-xs rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                >
                  {LANGUAGE_OPTIONS.map((code) => (
                    <option key={code} value={code}>{t(`settings.language.${code}`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">{t('settings.accent')}</label>
                <div className="flex flex-wrap gap-2">
                  {ACCENT_OPTIONS.map((accent) => {
                    const gradientClass =
                      accent === 'violet' ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white'
                      : accent === 'blue' ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
                      : accent === 'emerald' ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white'
                      : accent === 'red' ? 'bg-gradient-to-r from-red-600 to-red-500 text-white'
                      : accent === 'orange' ? 'bg-gradient-to-r from-orange-600 to-amber-500 text-white'
                      : accent === 'yellow' ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black/80'
                      : 'bg-gradient-to-r from-red-500 via-amber-400 to-violet-500 text-white'
                    return (
                      <button
                        key={accent}
                        type="button"
                        onClick={() => prefs.setAccent(accent)}
                        className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                          prefs.accent === accent
                            ? 'ring-2 ring-white/40 ring-offset-2 ring-offset-studio-bg'
                            : 'opacity-80 hover:opacity-100'
                        } ${gradientClass}`}
                      >
                        {t(`settings.accent.${accent}`)}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">{t('settings.dateFormat')}</label>
                <select
                  value={prefs.dateFormat}
                  onChange={(e) => prefs.setDateFormat(e.target.value)}
                  className="w-full max-w-xs rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                >
                  <option value="short">{t('settings.dateFormat.short')}</option>
                  <option value="medium">{t('settings.dateFormat.medium')}</option>
                  <option value="long">{t('settings.dateFormat.long')}</option>
                </select>
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={prefs.compactList}
                  onChange={(e) => prefs.setCompactList(e.target.checked)}
                  className="rounded border-white/20 bg-white/5"
                />
                {t('settings.compactList')}
              </label>
            </div>
          </Card>
        )}

        {activeTab === 'profile' && (
          <Card>
            <SectionTitle className="mb-4">Dein Konto</SectionTitle>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <dt className="text-white/50">E-Mail</dt>
              <dd className="text-white/90">{user?.email ?? '–'}</dd>
              <dt className="text-white/50">Rolle</dt>
              <dd className="text-white/90">{user?.is_admin ? 'Administrator' : 'Benutzer'}</dd>
            </dl>
            <label className="mt-6 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={emailOnJobDone}
                disabled={emailOnJobDoneSaving}
                onChange={async (e) => {
                  const next = e.target.checked
                  setEmailOnJobDoneSaving(true)
                  try {
                    await api.patch('/me', { email_on_job_done: next })
                    setEmailOnJobDone(next)
                    toast(next ? 'E-Mail bei fertigem Job aktiviert' : 'E-Mail bei fertigem Job deaktiviert', 'success')
                  } catch (err) {
                    toast(err?.message || 'Speichern fehlgeschlagen', 'error')
                  } finally {
                    setEmailOnJobDoneSaving(false)
                  }
                }}
                className="rounded border-white/20 bg-white/5 text-violet-500 focus:ring-violet-500/20"
              />
              <span className="text-sm text-white/80">E-Mail, wenn ein Job fertig ist (Mastering, Cover, Canvas)</span>
            </label>
            <p className="mt-4 text-xs text-white/50">
              E-Mail und Passwort unter „Benutzerverwaltung“ bearbeiten (als Admin: sich selbst anklicken).
            </p>
          </Card>
        )}

        {activeTab === 'privacy' && (
          <Card>
            <SectionTitle className="mb-4">Datenschutz & Ihre Rechte (DSGVO)</SectionTitle>
            <p className="mb-6 text-sm text-white/70">
              Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Datenübertragbarkeit. Details stehen in der{' '}
              <Link to="/dashboard/datenschutz" className="text-violet-300 hover:underline">Datenschutzerklärung</Link>.
            </p>
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Daten exportieren (Art. 20 DSGVO)</h3>
                <p className="text-sm text-white/60 mb-3">
                  Laden Sie eine JSON-Datei mit Ihren Kontodaten und der Übersicht Ihrer Tracks und Jobs herunter.
                </p>
                <SecondaryButton onClick={handleExportData} disabled={exportLoading}>
                  {exportLoading ? 'Exportiere…' : 'Meine Daten exportieren'}
                </SecondaryButton>
              </div>
              <div className="pt-6 border-t border-white/10">
                <h3 className="text-sm font-semibold text-white mb-2">Konto löschen (Art. 17 DSGVO)</h3>
                <p className="text-sm text-white/60 mb-3">
                  Löschung Ihres Kontos und aller zugehörigen Daten (Tracks, Jobs, Credits-Historie) – endgültig und nicht wiederherstellbar.
                </p>
                {deleteError && (
                  <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                    {deleteError}
                  </div>
                )}
                <form onSubmit={handleDeleteAccount} className="space-y-3 max-w-md">
                  <label className="block">
                    <span className="mb-1 block text-xs text-white/50">Aktuelles Passwort</span>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
                      placeholder="Passwort"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-white/50">Zur Bestätigung exakt eingeben: KONTO LÖSCHEN</span>
                    <input
                      type="text"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                      placeholder="KONTO LÖSCHEN"
                    />
                  </label>
                  <DangerButton type="submit" disabled={deleteLoading}>
                    {deleteLoading ? 'Löschen…' : 'Konto endgültig löschen'}
                  </DangerButton>
                </form>
              </div>
            </div>
          </Card>
        )}

        {activeTab === 'branding' && (
          <Card>
            <SectionTitle className="mb-4">Branding</SectionTitle>
            <p className="mb-6 text-sm text-white/60">
              Diese Einstellungen werden bei der Cover-Generierung verwendet (Künstler, Farben, Schrift, Layout).
            </p>
            {brandingError && (
              <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">
                {brandingError}
              </div>
            )}
            {brandingLoading ? (
              <p className="text-white/60">Laden…</p>
            ) : (
              <form onSubmit={saveBranding} className="space-y-6">
                <div>
                  <label className="mb-1 block text-sm font-medium text-white/80">Anzeigename / Künstler</label>
                  <input
                    type="text"
                    value={brandingForm.brand_display_name ?? ''}
                    onChange={(e) => setBrandingForm((f) => ({ ...f, brand_display_name: e.target.value }))}
                    placeholder="z. B. Künstlername"
                    className="w-full max-w-md rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-white/80">Tagline (optional)</label>
                  <input
                    type="text"
                    value={brandingForm.brand_tagline ?? ''}
                    onChange={(e) => setBrandingForm((f) => ({ ...f, brand_tagline: e.target.value }))}
                    placeholder="Kurzer Slogan"
                    className="w-full max-w-md rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-white/80">Logo (optional, max. 2 MB)</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={uploadBrandLogo}
                      disabled={logoUploading}
                      className="text-sm text-white/80 file:mr-2 file:rounded-lg file:border-0 file:bg-violet-600 file:px-3 file:py-1.5 file:text-white"
                    />
                    {logoUploading && <span className="text-sm text-white/60">Hochladen…</span>}
                    {brandingData?.brand_logo && (
                      <span className="text-sm text-white/50">Aktuell: {brandingData.brand_logo}</span>
                    )}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Primärfarbe (Hex)</label>
                    <input
                      type="text"
                      value={brandingForm.palette_primary ?? ''}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, palette_primary: e.target.value }))}
                      placeholder="#3b82f6"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Akzentfarbe (Hex)</label>
                    <input
                      type="text"
                      value={brandingForm.palette_accent ?? ''}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, palette_accent: e.target.value }))}
                      placeholder="#a855f7"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Hintergrund (Hex)</label>
                    <input
                      type="text"
                      value={brandingForm.palette_background ?? ''}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, palette_background: e.target.value }))}
                      placeholder="#0f172a"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Textfarbe (Hex)</label>
                    <input
                      type="text"
                      value={brandingForm.palette_text ?? ''}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, palette_text: e.target.value }))}
                      placeholder="#f8fafc"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Schrift Primär</label>
                    <select
                      value={brandingForm.font_primary ?? 'Inter'}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, font_primary: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    >
                      {['Inter', 'Montserrat', 'Bebas Neue', 'Poppins'].map((font) => (
                        <option key={font} value={font}>{font}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Schrift Sekundär</label>
                    <select
                      value={brandingForm.font_secondary ?? 'Inter'}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, font_secondary: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    >
                      {['Inter', 'Montserrat', 'Bebas Neue', 'Poppins'].map((font) => (
                        <option key={font} value={font}>{font}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Cover-Layout</label>
                    <select
                      value={brandingForm.cover_layout_preset ?? 'center'}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, cover_layout_preset: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    >
                      {['top_left', 'top_center', 'center', 'bottom_center', 'bottom_left'].map((opt) => (
                        <option key={opt} value={opt}>{opt.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">Cover-Textstil</label>
                    <select
                      value={brandingForm.cover_text_style ?? 'minimal'}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, cover_text_style: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                    >
                      {['minimal', 'bold', 'elegant', 'techno'].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-white/80">Brand-Prompt (optional, an Bild-Prompt angehängt)</label>
                  <textarea
                    value={brandingForm.brand_prompt ?? ''}
                    onChange={(e) => setBrandingForm((f) => ({ ...f, brand_prompt: e.target.value }))}
                    placeholder="Zusätzliche Beschreibung für die KI"
                    rows={2}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-white/80">Standard-Genre für Covers</label>
                  <select
                    value={brandingForm.default_genre_style ?? 'melodic_house'}
                    onChange={(e) => setBrandingForm((f) => ({ ...f, default_genre_style: e.target.value }))}
                    className="w-full max-w-xs rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                  >
                    {['melodic_house', 'afro_house', 'deep_house', 'techno'].map((opt) => (
                      <option key={opt} value={opt}>{opt.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-white/80">
                    <input
                      type="checkbox"
                      checked={brandingForm.watermark_enabled ?? false}
                      onChange={(e) => setBrandingForm((f) => ({ ...f, watermark_enabled: e.target.checked }))}
                      className="rounded border-white/20 bg-white/5"
                    />
                    Wasserzeichen aktiv
                  </label>
                  <input
                    type="text"
                    value={brandingForm.watermark_text ?? ''}
                    onChange={(e) => setBrandingForm((f) => ({ ...f, watermark_text: e.target.value }))}
                    placeholder="Wasserzeichen-Text"
                    className="max-w-xs rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <SecondaryButton type="button" onClick={loadBranding}>Aktualisieren</SecondaryButton>
                  <PrimaryButton type="submit" disabled={brandingSaving}>{brandingSaving ? 'Speichern…' : 'Speichern'}</PrimaryButton>
                </div>
              </form>
            )}
          </Card>
        )}

        {activeTab === 'users' && isAdmin && (
          <Card>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <SectionTitle>Benutzer</SectionTitle>
              <PrimaryButton onClick={openCreate}>Benutzer anlegen</PrimaryButton>
            </div>
            {userError && (
              <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">
                {userError}
              </div>
            )}
            {usersLoading ? (
              <p className="text-white/60">Laden…</p>
            ) : (
              <DataTable>
                <DataTableHead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-white/60">E-Mail</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-white/60">Rolle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-white/60">Aktiv</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase text-white/60">Aktionen</th>
                  </tr>
                </DataTableHead>
                <DataTableBody>
                  {users.map((u, i) => (
                    <DataTableRow key={u.id} zebra={i % 2 === 1}>
                      <DataTableCell>{u.email}</DataTableCell>
                      <DataTableCell>{u.is_admin ? <Badge variant="ready">Admin</Badge> : 'Benutzer'}</DataTableCell>
                      <DataTableCell>{u.is_active ? 'Ja' : 'Nein'}</DataTableCell>
                      <DataTableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <SecondaryButton onClick={() => openEdit(u)}>Bearbeiten</SecondaryButton>
                          {u.id !== user?.id && (
                            <DangerButton onClick={() => deleteUser(u.id)}>Löschen</DangerButton>
                          )}
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            )}
          </Card>
        )}

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeForm}>
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg font-semibold text-white">{editId ? 'Benutzer bearbeiten' : 'Benutzer anlegen'}</h3>
              {userError && (
                <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                  {userError}
                </div>
              )}
              <form onSubmit={saveUser} className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-xs text-white/50">E-Mail</span>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    required
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
                  />
                </label>
                {!editId && (
                  <label className="block">
                    <span className="mb-1 block text-xs text-white/50">Passwort</span>
                    <input
                      type="password"
                      value={formPassword}
                      onChange={(e) => setFormPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
                    />
                  </label>
                )}
                {editId && (
                  <label className="block">
                    <span className="mb-1 block text-xs text-white/50">Neues Passwort (leer = unverändert)</span>
                    <input
                      type="password"
                      value={formPassword}
                      onChange={(e) => setFormPassword(e.target.value)}
                      placeholder="Optional"
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                    />
                  </label>
                )}
                <label className="flex cursor-pointer items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} className="rounded" />
                  Aktiv
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={formIsAdmin} onChange={(e) => setFormIsAdmin(e.target.checked)} className="rounded" />
                  Administrator
                </label>
                {editId === user?.id && <p className="text-xs text-white/50">Eigene Admin-Rolle nicht entziehbar.</p>}
                <div className="flex gap-2 pt-2">
                  <SecondaryButton type="button" onClick={closeForm}>Abbrechen</SecondaryButton>
                  <PrimaryButton type="submit" disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</PrimaryButton>
                </div>
              </form>
            </Card>
          </div>
        )}
      </div>
    </PageShell>
  )
}
